
import React from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface NoResultsProps {
  selectedCategory: string;
  setShowCreate: React.Dispatch<React.SetStateAction<boolean>>;
}

// Category examples to show when no results are found
const categoryExamples = {
  "voirie": {
    title: "Trottoir endommagé",
    description: "Le trottoir est gravement endommagé à l'intersection des rues du Commerce et Victor Hugo. Plusieurs dalles sont cassées et créent un risque de chute pour les piétons.",
    image: "https://images.unsplash.com/photo-1516583641623-7d9934d86e26?q=80&w=400&auto=format"
  },
  "proprete": {
    title: "Poubelles débordantes",
    description: "Les poubelles débordent depuis plusieurs jours à proximité de l'école Jules Ferry, attirant des nuisibles et créant une situation insalubre.",
    image: "https://images.unsplash.com/photo-1562077981-4d7eafd44932?q=80&w=400&auto=format"
  },
  "eclairage": {
    title: "Lampadaire défectueux",
    description: "Le lampadaire au coin de la rue des Lilas clignote la nuit et s'éteint fréquemment, rendant le passage dangereux pour les piétons.",
    image: "https://images.unsplash.com/photo-1563501047022-d303f396d801?q=80&w=400&auto=format"
  },
  "transport": {
    title: "Arrêt de bus dégradé",
    description: "L'abri bus de la ligne 42 est fortement dégradé, le banc est cassé et le toit ne protège plus des intempéries.",
    image: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=400&auto=format"
  },
  "espaces-verts": {
    title: "Arbre dangereux",
    description: "Un arbre au parc municipal présente des branches cassées qui menacent de tomber sur l'aire de jeux pour enfants après la tempête de la semaine dernière.",
    image: "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=400&auto=format"
  },
  "eau": {
    title: "Fuite d'eau importante",
    description: "Une fuite d'eau importante est visible sur la chaussée rue des Peupliers depuis 3 jours, créant une flaque permanente et du verglas la nuit.",
    image: "https://images.unsplash.com/photo-1559825481-12a05cc00344?q=80&w=400&auto=format"
  },
  "securite": {
    title: "Passage piéton dangereux",
    description: "Le passage piéton près de l'école primaire est effacé et mal signalé, créant une situation dangereuse pour les enfants qui traversent.",
    image: "https://images.unsplash.com/photo-1551851735-d25c9dde4f3a?q=80&w=400&auto=format"
  },
  "pollution-air": {
    title: "Émissions suspectes d'une usine",
    description: "L'usine située dans la zone industrielle émet des fumées noires et malodorantes en dehors des horaires habituels, provoquant des difficultés respiratoires chez les riverains.",
    image: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?q=80&w=400&auto=format"
  },
  "pollution-eau": {
    title: "Mousse suspecte dans la rivière",
    description: "Une mousse blanchâtre et des irisations sont visibles à la surface de la rivière au niveau du pont central, probablement due à un déversement de polluants en amont.",
    image: "https://images.unsplash.com/photo-1565372595775-487219841e0c?q=80&w=400&auto=format"
  },
  "nuisance-sonore": {
    title: "Bruit nocturne excessif",
    description: "Le bar 'Le Nautilus' diffuse de la musique à un volume excessif jusqu'à 3h du matin malgré plusieurs plaintes des riverains, et ce bien au-delà des horaires autorisés.",
    image: "https://images.unsplash.com/photo-1578070181910-f1e514afdd08?q=80&w=400&auto=format"
  },
  "biodiversite": {
    title: "Destruction d'habitat naturel",
    description: "Des travaux non autorisés détruisent une zone humide abritant des espèces protégées au nord du parc naturel. Plusieurs nids d'oiseaux ont déjà été détruits.",
    image: "https://images.unsplash.com/photo-1500651230702-0e2d8a49d4ad?q=80&w=400&auto=format"
  },
  "flore-invasive": {
    title: "Présence d'ambroisie",
    description: "Une importante quantité d'ambroisie (plante hautement allergène) s'est développée près de l'école primaire et du stade municipal, causant des réactions allergiques chez plusieurs enfants.",
    image: "https://images.unsplash.com/photo-1541426402073-d0f88c20766d?q=80&w=400&auto=format"
  },
  "inondation": {
    title: "Zone régulièrement inondée",
    description: "Le passage souterrain de la rue des Saules est régulièrement inondé lors des fortes pluies, rendant la circulation impossible et dangereuse.",
    image: "https://images.unsplash.com/photo-1547683905-f686c993aae5?q=80&w=400&auto=format"
  },
  "ilot-chaleur": {
    title: "Place minérale surchauffée",
    description: "La place de la mairie, entièrement minérale, devient inhabitable en été avec des températures dépassant 40°C à l'ombre. Des aménagements ombragés sont nécessaires.",
    image: "https://images.unsplash.com/photo-1532229497546-365814186f52?q=80&w=400&auto=format"
  },
  "erosion": {
    title: "Berge de rivière qui s'effondre",
    description: "La berge de la rivière au niveau du chemin de halage s'érode dangereusement et menace de s'effondrer, mettant en danger les promeneurs et cyclistes.",
    image: "https://images.unsplash.com/photo-1469122312224-c5846569feb1?q=80&w=400&auto=format"
  },
  "ventilation": {
    title: "Problème d'aération dans tunnel",
    description: "Le système de ventilation du tunnel piétonnier sous la voie ferrée est défaillant, créant une atmosphère confinée et désagréable.",
    image: "https://images.unsplash.com/photo-1499364615650-ec38552f4f34?q=80&w=400&auto=format"
  },
  "pollution-visuelle": {
    title: "Affichage sauvage massif",
    description: "Les murs du centre culturel sont couverts d'affiches sauvages et de graffitis, dégradant l'aspect visuel de ce bâtiment historique.",
    image: "https://images.unsplash.com/photo-1613316136609-2cc00f0d42c0?q=80&w=400&auto=format"
  },
  "commerce-alimentaire": {
    title: "Hygiène douteuse d'un commerce",
    description: "Des conditions d'hygiène préoccupantes ont été observées dans la boulangerie 'Au bon pain', avec présence de nuisibles et manipulation d'aliments sans gants.",
    image: "https://images.unsplash.com/photo-1578916171728-46686eac8d58?q=80&w=400&auto=format"
  },
  "commerce": {
    title: "Local commercial abandonné",
    description: "L'ancien local du fleuriste rue Principale est abandonné depuis plus d'un an, devenant un point de dépôt de déchets et dégradant l'attractivité commerciale du quartier.",
    image: "https://images.unsplash.com/photo-1604719312566-8912e9667d17?q=80&w=400&auto=format"
  },
  "urgence": {
    title: "Câble électrique tombé",
    description: "Un câble électrique est tombé sur la voie publique suite aux vents violents d'hier soir, créant un danger immédiat pour les passants rue des Acacias.",
    image: "https://images.unsplash.com/photo-1638258581353-0a65d1506a0e?q=80&w=400&auto=format"
  },
  "terrain-abandonne": {
    title: "Friche industrielle dangereuse",
    description: "L'ancienne usine textile est laissée à l'abandon et présente des risques d'effondrement. Des jeunes y pénètrent régulièrement malgré le danger.",
    image: "https://images.unsplash.com/photo-1628447484139-f7294ebce186?q=80&w=400&auto=format"
  },
  "nid-de-poule": {
    title: "Nid-de-poule dangereux",
    description: "Un nid-de-poule profond est apparu après les dernières pluies et pose un danger pour les véhicules et les cyclistes au niveau du 42 avenue Jean Jaurès.",
    image: "https://images.unsplash.com/photo-1578385172288-d3bbcba61086?q=80&w=400&auto=format"
  },
  "signalisation": {
    title: "Panneau stop manquant",
    description: "Le panneau stop a disparu au carrefour des rues Lafayette et République, créant une situation dangereuse pour les automobilistes et les piétons.",
    image: "https://images.unsplash.com/photo-1577424148093-1f7be763240d?q=80&w=400&auto=format"
  },
  "accessibilite": {
    title: "Rampe d'accès bloquée",
    description: "La rampe d'accès pour personnes à mobilité réduite est bloquée par des travaux à l'entrée de la mairie, rendant impossible l'accès aux services publics.",
    image: "https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=400&auto=format"
  },
  "caca-de-chien": {
    title: "Déjections canines récurrentes",
    description: "Les trottoirs de la rue des Écoles sont régulièrement souillés par des déjections canines, rendant le passage désagréable pour les riverains et les écoliers.",
    image: "https://images.unsplash.com/photo-1583336663277-620dc1996580?q=80&w=400&auto=format"
  },
  "depot-sauvage": {
    title: "Dépôt d'encombrants illégal",
    description: "Un amas d'encombrants (canapé, matelas, électroménager) a été déposé illégalement sur le trottoir à l'angle de la rue des Peupliers depuis plus d'une semaine.",
    image: "https://images.unsplash.com/photo-1504713721722-fb81e7a4c59a?q=80&w=400&auto=format"
  },
  "mobilier-urbain": {
    title: "Banc public cassé",
    description: "Le banc public face à la pharmacie est cassé et présente des éclats de bois dangereux. Plusieurs personnes âgées utilisent ce banc quotidiennement.",
    image: "https://images.unsplash.com/photo-1613321821704-fca55f8d24ba?q=80&w=400&auto=format"
  }
};

export const NoResults: React.FC<NoResultsProps> = ({ selectedCategory, setShowCreate }) => {
  // If we have a category example, show it as a suggestion
  if (selectedCategory && categoryExamples[selectedCategory]) {
    const example = categoryExamples[selectedCategory];
    
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center text-center py-8 max-w-2xl mx-auto"
      >
        <h3 className="text-xl font-bold mb-2">Exemple de signalement: {example.title}</h3>
        <div className="bg-white rounded-lg shadow-md overflow-hidden w-full max-w-md mb-4">
          {example.image && (
            <img 
              src={example.image} 
              alt={example.title} 
              className="w-full h-48 object-cover"
            />
          )}
          <div className="p-6">
            <p className="text-gray-700 mb-4">{example.description}</p>
            <div className="flex justify-center">
              <Button 
                onClick={() => setShowCreate(true)} 
                className="bg-primary"
                size="sm"
              >
                Créer un signalement similaire
              </Button>
            </div>
          </div>
        </div>
        <p className="text-gray-500 mt-4 text-sm max-w-md">
          Aucun signalement existant ne correspond à vos critères de recherche.
          Vous pouvez créer un nouveau signalement dans cette catégorie.
        </p>
      </motion.div>
    );
  }
  
  // Generic no results message
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center py-8"
    >
      <p className="text-gray-500">
        Aucun signalement ne correspond à vos critères de recherche.
      </p>
    </motion.div>
  );
};
